import { useCallback, useEffect, useRef, useState } from 'react'
import { Link, NavLink, useLocation } from 'react-router-dom'
import Avatar from './Avatar.jsx'
import ThemeToggle from './ThemeToggle.jsx'
import { useAuth } from '../context/AuthContext.jsx'
import { api } from '../lib/api.js'

/**
 * The nav carries only routes that exist. The admin link appears for the
 * owner and for nobody else — a link to a 403 is a worse answer than no link.
 *
 * The unread badge is fetched once per mount rather than polled. A closed
 * alpha does not need a live count, and a poll is a request every reader
 * makes forever for a number that is usually zero.
 *
 * **`.nav-links` is a horizontal scroller and always has been** — at 390px it
 * shows about 230px of some 620px of destinations. That is a deliberate shape
 * and not the defect; the defect was that nothing said so. It loaded at
 * `scrollLeft: 0` on every page, so on anything but the feed the item for the
 * page you were on sat off to the right and the nav appeared to have no
 * current page at all. The two effects below are the whole fix: put the active
 * link in view, and mark which edges have more behind them so the strip can
 * show it.
 *
 * The unread badge is the one thing that fade defeats. It lives inside the
 * Notifications link, sixth in the strip, so at a phone width it is behind the
 * mask on every load — a badge whose whole purpose is to be seen. `markEdges`
 * already measures the strip's visible box, so it marks one thing more: which
 * edge the Notifications link is past, if any. `.nav-strip` draws a dot there.
 */
export default function NavBar() {
  const { user } = useAuth()
  const location = useLocation()
  const [unread, setUnread] = useState(0)
  const scroller = useRef(null)
  // The dot's anchor. It cannot be drawn inside `.nav-links`: the fade is a
  // `mask-image` on that element, and a mask fades everything rendered in it,
  // so a dot at the edge would sit in the most transparent 28px of its own
  // affordance. The wrapper is outside the mask.
  const strip = useRef(null)

  // Which edges have content beyond them. The stylesheet turns this into a
  // fade; on a desktop-width nav nothing overflows and it stays "none", so the
  // fade costs nothing where it is not needed.
  const markEdges = useCallback(() => {
    const el = scroller.current
    if (!el) return
    const slack = el.scrollWidth - el.clientWidth
    const start = el.scrollLeft > 1
    const end = el.scrollLeft < slack - 1
    el.dataset.overflow = slack < 2 ? 'none' : [start && 'start', end && 'end'].filter(Boolean).join(' ')

    // And which edge the unread badge is behind, if it is behind one. Both
    // edges, because the active-link effect above scrolls Settings into view
    // and puts Notifications off the *start* — an end-only dot would go quiet
    // on the route where somebody is least likely to be thinking about their
    // notifications. Rects rather than `offsetLeft`, for the same reason that
    // effect gives.
    const wrap = strip.current
    if (!wrap) return
    const bell = el.querySelector('a[href="/notifications"]')
    let edge = null
    if (slack >= 2 && unread > 0 && bell) {
      const box = el.getBoundingClientRect()
      const link = bell.getBoundingClientRect()
      if (link.right <= box.left + 1) edge = 'start'
      else if (link.left >= box.right - 1) edge = 'end'
    }
    if (edge) wrap.dataset.unreadEdge = edge
    else delete wrap.dataset.unreadEdge
  }, [unread])

  // The active link, brought into view on every navigation. `scrollIntoView`
  // is not used: it would scroll the page as well as the strip, and this nav
  // is sticky, so that reads as the page jumping under a tap.
  useEffect(() => {
    const el = scroller.current
    if (!el) return undefined
    const active = el.querySelector('.nav-link.active')
    if (active) {
      // Measured as rectangles rather than through `offsetLeft`: the strip is
      // not a positioned ancestor, so `offsetLeft` is counted from the nav
      // wrapper and silently includes the brand's width. That put the feed's
      // own link 54px off centre on the page where it should not move at all.
      const strip = el.getBoundingClientRect()
      const link = active.getBoundingClientRect()
      const delta = link.left - strip.left - (el.clientWidth - link.width) / 2
      el.scrollLeft = Math.max(0, el.scrollLeft + delta)
    }
    markEdges()
    return undefined
  }, [location.pathname, user, unread, markEdges])

  useEffect(() => {
    const el = scroller.current
    if (!el) return undefined
    el.addEventListener('scroll', markEdges, { passive: true })
    window.addEventListener('resize', markEdges)
    return () => {
      el.removeEventListener('scroll', markEdges)
      window.removeEventListener('resize', markEdges)
    }
  }, [markEdges])

  useEffect(() => {
    if (!user) return undefined
    const controller = new AbortController()
    api
      .notifications({ signal: controller.signal })
      .then((body) => setUnread(body.unread))
      .catch(() => {})
    return () => controller.abort()
  }, [user])

  const link = ({ isActive }) => `nav-link ${isActive ? 'active' : ''}`

  return (
    <nav className="nav" aria-label="Primary">
      <div className="nav-inner">
        <NavLink to="/" className="brand">
          <span className="brand-mark" aria-hidden="true">
            ◍
          </span>
          Sonologue
        </NavLink>

        <div className="nav-strip" ref={strip}>
          <div className="nav-links" ref={scroller} data-overflow="none">
            <NavLink to="/" end className={link}>
              Feed
            </NavLink>
            <NavLink to="/search" className={link}>
              Search
            </NavLink>
            <NavLink to="/queue" className={link}>
              Queue
            </NavLink>
            <NavLink to="/charts" className={link}>
              Charts
            </NavLink>
            <NavLink to="/notifications" className={link}>
              Notifications
              {unread > 0 ? (
                <span className="badge" aria-label={`${unread} unread`}>
                  {unread > 9 ? '9+' : unread}
                </span>
              ) : null}
            </NavLink>
            {user ? (
              <NavLink to={`/profile/${user.username}`} className={link}>
                Profile
              </NavLink>
            ) : null}
            {user?.is_admin ? (
              <NavLink to="/admin/reports" className={link}>
                Reports
              </NavLink>
            ) : null}
            <NavLink to="/settings" className={link}>
              Settings
            </NavLink>
            <ThemeToggle />
            {user ? (
              // The avatar is the second route to the same page as the "Profile"
              // link beside it, and that is what people reach for — a portrait in
              // the corner of a nav reads as "me" everywhere else on the web. It
              // carries its own accessible name because `Avatar` is decorative
              // (`aria-hidden`), so without one the link would announce as empty.
              <Link
                to={`/profile/${user.username}`}
                className="nav-avatar ml-2"
                aria-label="Your profile"
              >
                <Avatar
                  name={user.display_name || user.username}
                  avatarUrl={user.avatar_url}
                  size={28}
                />
              </Link>
            ) : null}
          </div>
        </div>
      </div>
    </nav>
  )
}
